// src/contexts/AuthContext.js

import React, { createContext, useContext, useEffect, useState } from 'react'
import { auth, db } from '../firebase'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  sendEmailVerification
} from 'firebase/auth'
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore'

const AuthContext = createContext()

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authed, setAuthed] = useState(false)
  const [userStatus, setUserStatus] = useState(null)

  // 1) Email/Password ile yeni kullanıcı kaydı (Register)
  async function register(email, password, nickname) {
    // Firebase Authentication’da kullanıcı oluştur
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const { uid } = userCredential.user

    // Firestore’daki "users" koleksiyonuna kullanıcı bilgilerini kaydet
    await setDoc(doc(db, 'users', uid), {
      uid,
      email,
      avatarUrl: '',
      nickname: nickname || '',
      role: 'user',
      status: 'active',
      createdAt: serverTimestamp(),
      emailVerified: false, // <-- email doğrulanmadı olarak başlatıyoruz
    })

    // Email doğrulama maili gönder
    await sendEmailVerification(userCredential.user)

    // Kayıttan sonra kullanıcıyı otomatik çıkış yaptır (isteğe bağlı)
    await signOut(auth)

    return userCredential
  }

  // 2) Email/Password ile Giriş (Login)
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password)
  }

  // 3) Şifre sıfırlama
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email)
  }

  // 4) Çıkış yapma
  function logout() {
    return signOut(auth)
  }

  // 5) Mevcut kullanıcıyı izleme (onAuthStateChanged)
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (user.isAnonymous) {
          setCurrentUser({
            uid: user.uid,
            email: '',
            avatarUrl: '',
            nickname: '',
            isAnonymous: true,
          })
          setUserRole('guest')
          setUserStatus('active')
          setAuthed(true)
          setLoading(false)
          return
        }

        // Eğer Firebase tarafında emailVerified true ise Firestore da güncellenir
        if (user.emailVerified) {
          const userDocRef = doc(db, 'users', user.uid)
          const userDocSnap = await getDoc(userDocRef)
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data()       
            if (!userData?.emailVerified) {
                  await updateDoc(userDocRef, { 
                    emailVerified: true,
                  })                              
            }
          }
        }

        // Ardından Firestore'daki kullanıcının verilerini dinle
        const userDocRef = doc(db, 'users', user.uid)
        const unsubscribeUserDoc = onSnapshot(userDocRef, (snapshot) => {
          if (snapshot.exists()) {

            const userData = snapshot.data()
            setCurrentUser({
              uid: user.uid,
              email: user.email,
              avatarUrl: userData.avatarUrl || '',
              nickname: userData.nickname || '',
              isAnonymous: false,
            })
            setUserRole(userData.role || 'user')
            setUserStatus(userData.status || 'active')
          } else {
            // Kullanıcı Firestore’da yoksa Auth’tan gelen verileri setleyelim
            setCurrentUser({
              uid: user.uid,
              email: user.email,
              avatarUrl: user.avatarUrl || '',
              nickname: user.nickname || '',
              isAnonymous: false,
            })
            setUserRole('user')
            setUserStatus('active')
          }
          setAuthed(true)
          setLoading(false)
        })

        return () => {
          unsubscribeUserDoc()
        }
      } else {
        // Kullanıcı yoksa state sıfırla
        setCurrentUser(null)
        setUserRole(null)
        setUserStatus(null)
        setAuthed(false)
        setLoading(false)
      }
    })

    // Cleanup
    return () => {
      unsubscribeAuth()
    }
  }, [])

  // Context olarak döndüreceğimiz değerler
  const value = {
    currentUser,
    userRole,
    authed,
    userStatus,
    setAuthed,
    register,
    login,
    resetPassword,
    logout,
  }

  return (
    <AuthContext.Provider value={value}>
      {/* loading false olana kadar children'ı göstermeyelim */}
      {!loading && children}
    </AuthContext.Provider>
  )
}
